import React, { useEffect } from 'react';
import { Navigate } from 'react-router';
import styled from 'styled-components';

import { useMarketTickerQuery } from 'api/queries/tickersQueries';
import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import { useRouterLocation } from 'components/hooks';
import { registerOrderBookService } from 'components/orders/orderBook/orderBookService';
import { useIsMobileBuySellRoute } from 'components/trading/dashboardDetect';
import { buildMarketPair } from 'lib/display/formatting';
import { switchCurrentMarket } from 'reduxState/markets/currentMarket';
import { getCurrentMarketByPath } from 'reduxState/markets/currentMarketUtils';
import {
  getDashboardUrl,
  useSelectMarketTickSize,
} from 'reduxState/markets/listed/listedMarketsUtils';
import { useSelector, staticDispatch as dispatch } from 'reduxState/store';

import DashboardDesktop from './DashboardDesktop';
import DashboardMobile from './DashboardMobile';

const H1 = styled.h1`
  text-align: center;
  padding-top: 50px;
`;

const ErrorIconStyled = styled(MaterialSymbol)`
  text-align: center;
  margin-bottom: 20px;
`;

function InnerDashboardPage({
  baseSymbol,
  quoteSymbol,
}: {
  baseSymbol?: string;
  quoteSymbol?: string;
}) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const currentMarket = useSelector(state => state.markets.currentMarket);
  const tickSize = useSelectMarketTickSize(currentMarket.pair);
  const isMobileBuySellRoute = useIsMobileBuySellRoute();

  const { data: currentMarketTicker } = useMarketTickerQuery({
    market: currentMarket.pair,
    websocketSubscribe: true,
  });

  const marketTickSizeInPip = tickSize?.inPip;
  const marketTickSizeVisibleDecimals = tickSize?.visibleDecimals;

  // Register Orderbook for all subpages of Dashboard
  useEffect(() => {
    if (
      marketTickSizeInPip === undefined ||
      marketTickSizeVisibleDecimals === undefined
    ) {
      return;
    }
    if (currentMarket.pair) {
      const service = registerOrderBookService(
        currentMarket.pair,
        marketTickSizeInPip,
        marketTickSizeVisibleDecimals
      );
      return service.unregister;
    }
  }, [currentMarket.pair, marketTickSizeInPip, marketTickSizeVisibleDecimals]);

  if (currentMarketTicker === false) {
    return (
      <H1>
        <ErrorIconStyled icon="error" fontSize="large" />
        <br />
        Market {baseSymbol}-{quoteSymbol} not found
      </H1>
    );
  }

  if (isMobileBuySellRoute && !isMobile) {
    return (
      <Navigate
        to={getDashboardUrl(
          'katanaEVMChain',
          buildMarketPair(baseSymbol, quoteSymbol)
        )}
      />
    );
  }

  return isMobile ? <DashboardMobile /> : <DashboardDesktop />;
}

function DashboardPage() {
  const location = useRouterLocation();
  const { baseSymbol, quoteSymbol } =
    getCurrentMarketByPath(location?.pathname) || {};

  const currentMarket = useSelector(state => state.markets.currentMarket);

  const smoothSwitch = location.hash === '#switch';
  const marketSynced =
    currentMarket.base === baseSymbol && currentMarket.quote === quoteSymbol;

  useEffect(() => {
    if (smoothSwitch && marketSynced) {
      // Remove #switch from url
      window.history.replaceState(null, document.title, ' ');
    }
  }, [smoothSwitch, marketSynced]);

  useEffect(() => {
    if (!baseSymbol || !quoteSymbol) {
      return;
    }
    // From this and only place we merge react-router Dashboard path and our store information about market
    if (!marketSynced) {
      console.log('Triggered market switch from URL', baseSymbol, quoteSymbol);
      dispatch(switchCurrentMarket(baseSymbol, quoteSymbol));
    }
  }, [baseSymbol, quoteSymbol, marketSynced]);

  if (!baseSymbol || !quoteSymbol) {
    console.error(
      `Market not set by URL - unexpected error, pathname=${location?.pathname}`
    );
    return null;
  }

  if (baseSymbol && quoteSymbol && !marketSynced) {
    // This case will be automatically fixed by hooks above
    console.warn(
      `Market not in sync with URL for pathname=${location?.pathname} and state=${currentMarket?.pair}`
    );
    if (!smoothSwitch) {
      // When smooth switch is active, we have to keep dashboard rendered
      return null;
    }
  }

  return (
    <InnerDashboardPage baseSymbol={baseSymbol} quoteSymbol={quoteSymbol} />
  );
}

export default DashboardPage;
